import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useGetHelpTreeQuery, HelpTreeNode } from "../api";

type Scope = "public" | "private";

const renderTree = (nodes: HelpTreeNode[], depth = 0) => {
    return nodes.map((node) => (
        <div
            key={node.id}
            className="border-b border-gray-100 dark:border-gray-700 py-1 pl-2"
            style={{ paddingLeft: `${depth * 16}px` }}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 pl-2">
                    <span className="text-xs text-gray-400">
                        {node.type === "section" ? "📂" : "📄"}
                    </span>
                    <div>
                        <div className="text-sm font-medium">
                            {node.title}{" "}
                            <span className="text-xs text-gray-400">
                                ({node.full_path})
                            </span>
                        </div>
                        <div className="text-xs text-gray-400">
                            {node.scope} · {node.locale} ·{" "}
                            {node.is_published ? "опубликовано" : "черновик"}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {node.type === "section" && (
                        <Link
                            to={`/dashboard/help/new?parent_id=${node.id}&scope=${node.scope}&locale=${node.locale}`}
                            className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-700 hover:bg-green-100"
                        >
                            + подраздел
                        </Link>
                    )}
                    <Link
                        to={`/dashboard/help/${node.id}`}
                        className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                        Редактировать
                    </Link>
                </div>
            </div>
            {node.children && node.children.length > 0 && (
                <div className="mt-1">
                    {renderTree(node.children, depth + 1)}
                </div>
            )}
        </div>
    ));
};

const HelpTreePage: React.FC = () => {
    const [scope, setScope] = useState<Scope>(() => {
        if (typeof window === "undefined") {
            return "private";
        }
        const stored = window.localStorage.getItem("helpTreeScope");
        if (stored === "public" || stored === "private") {
            return stored;
        }

        return "private";
    });
    const [locale, setLocale] = useState<string>("ru");

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        window.localStorage.setItem("helpTreeScope", scope);
    }, [scope]);

    const { data, isLoading, isError, refetch } = useGetHelpTreeQuery({
        scope,
        locale,
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Справка (дерево)</h1>
                <div className="flex items-center gap-2">
                    <select
                        value={scope}
                        onChange={(e) => setScope(e.target.value as Scope)}
                        className="border rounded px-2 pr-8 py-1 text-sm"
                    >
                        <option value="public">Публичная</option>
                        <option value="private">Приватная</option>
                    </select>
                    <input
                        value={locale}
                        onChange={(e) => setLocale(e.target.value)}
                        className="border rounded px-2 py-1 text-sm w-16"
                        placeholder="ru"
                    />
                    <button
                        onClick={() => refetch()}
                        className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                    >
                        Обновить
                    </button>
                    <Link
                        to="/dashboard/help/new"
                        className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                    >
                        + Новый раздел/статья
                    </Link>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded shadow border border-gray-200 dark:border-gray-700">
                {isLoading && (
                    <div className="p-4 text-sm text-gray-500">Загрузка...</div>
                )}
                {isError && (
                    <div className="p-4 text-sm text-red-600">
                        Ошибка загрузки дерева справки.
                    </div>
                )}
                {!isLoading && !isError && data && data.length === 0 && (
                    <div className="p-4 text-sm text-gray-500">
                        Узлы справки пока не созданы.
                    </div>
                )}
                {!isLoading && !isError && data && data.length > 0 && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {renderTree(data)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default HelpTreePage;

